import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { CreditCard, CheckCircle, AlertCircle } from 'lucide-react';
import Button from './common/Button';
import Card from './common/Card';
import { createCheckoutSession, purchaseCredits, createCustomerPortalSession } from '../lib/stripe';
import { useAuth } from '../contexts/AuthContext';

const StripeTestComponent: React.FC = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState<string | null>(null);
  const [results, setResults] = useState<string[]>([]);

  const addResult = (message: string, success: boolean = true) => {
    const icon = success ? '✅' : '❌';
    setResults(prev => [...prev, `${icon} ${message}`]);
  };

  const testStripeCheckout = async (planType: 'starter' | 'pro') => {
    if (!user) {
      addResult('Please sign in first', false);
      return;
    }

    try {
      setLoading(planType);
      await createCheckoutSession(planType);
      addResult(`${planType} checkout initiated successfully`);
    } catch (error) {
      addResult(`${planType} checkout failed: ${error.message}`, false);
    } finally {
      setLoading(null);
    }
  };

  const testCreditsPurchase = async (amount: '100' | '500') => {
    if (!user) {
      addResult('Please sign in first', false);
      return;
    }

    try {
      setLoading(`credits-${amount}`);
      await purchaseCredits(amount);
      addResult(`${amount} credits purchase initiated successfully`);
    } catch (error) {
      addResult(`${amount} credits purchase failed: ${error.message}`, false);
    } finally {
      setLoading(null);
    }
  };

  const testCustomerPortal = async () => {
    if (!user) {
      addResult('Please sign in first', false);
      return;
    }

    try {
      setLoading('portal');
      await createCustomerPortalSession();
      addResult('Customer portal opened successfully');
    } catch (error) {
      addResult(`Customer portal failed: ${error.message}`, false);
    } finally {
      setLoading(null);
    }
  };

  const clearResults = () => setResults([]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="max-w-4xl mx-auto p-6"
    >
      <Card className="p-6">
        <div className="flex items-center mb-6">
          <CreditCard className="h-6 w-6 text-primary-600 mr-2" />
          <h2 className="text-2xl font-bold text-gray-800">Stripe Integration Test</h2>
        </div>

        {!user && (
          <div className="bg-warning-50 border-l-4 border-warning-500 p-4 mb-6">
            <div className="flex items-center">
              <AlertCircle className="h-5 w-5 text-warning-500 mr-2" />
              <p className="text-warning-700">Please sign in to test Stripe functionality</p>
            </div>
          </div>
        )}

        <div className="grid md:grid-cols-2 gap-6 mb-6">
          <div>
            <h3 className="text-lg font-semibold mb-4">Subscription Checkout</h3>
            <div className="space-y-3">
              <Button
                variant="primary"
                onClick={() => testStripeCheckout('starter')}
                isLoading={loading === 'starter'}
                disabled={!user}
                fullWidth
              >
                Test Starter Plan ($9/month)
              </Button>
              <Button
                variant="primary"
                onClick={() => testStripeCheckout('pro')}
                isLoading={loading === 'pro'}
                disabled={!user}
                fullWidth
              >
                Test Pro Plan ($29/month)
              </Button>
            </div>
          </div>

          <div>
            <h3 className="text-lg font-semibold mb-4">Credits Purchase</h3>
            <div className="space-y-3">
              <Button
                variant="outline"
                onClick={() => testCreditsPurchase('100')}
                isLoading={loading === 'credits-100'}
                disabled={!user}
                fullWidth
              >
                Test 100 Credits ($5)
              </Button>
              <Button
                variant="outline"
                onClick={() => testCreditsPurchase('500')}
                isLoading={loading === 'credits-500'}
                disabled={!user}
                fullWidth
              >
                Test 500 Credits ($20)
              </Button>
            </div>
          </div>
        </div>

        <div className="mb-6">
          <h3 className="text-lg font-semibold mb-4">Customer Portal</h3>
          <Button
            variant="outline"
            onClick={testCustomerPortal}
            isLoading={loading === 'portal'}
            disabled={!user}
            leftIcon={<CreditCard className="h-4 w-4" />}
          >
            Test Customer Portal
          </Button>
        </div>

        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">Test Results</h3>
          <Button variant="ghost" size="sm" onClick={clearResults}>
            Clear Results
          </Button>
        </div>

        <div className="bg-gray-50 rounded-lg p-4 min-h-[200px]">
          {results.length === 0 ? (
            <p className="text-gray-500 text-center">No tests run yet. Click the buttons above to test Stripe integration.</p>
          ) : (
            <div className="space-y-2">
              {results.map((result, index) => (
                <div key={index} className="font-mono text-sm">
                  {result}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="mt-6 p-4 bg-blue-50 rounded-lg">
          <h4 className="font-medium text-blue-800 mb-2">Integration Status</h4>
          <div className="text-sm text-blue-700 space-y-1">
            <div>✅ Stripe client library integrated</div>
            <div>✅ Checkout session creation implemented</div>
            <div>✅ Webhook handling configured</div>
            <div>✅ Customer portal integration ready</div>
            <div>✅ Credit purchase system implemented</div>
            <div>✅ Subscription management integrated</div>
          </div>
        </div>
      </Card>
    </motion.div>
  );
};

export default StripeTestComponent;